import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useBlockedUserIds } from "@/hooks/hub/useHubBlocks";

/**
 * LR HUB comments + one level of replies.
 *
 * READ: `hub_post_comments` — approved + non-deleted for everyone, plus the
 * caller's own in-review comments (RLS enforces the same rule server-side).
 *
 * WRITE: there is NO client INSERT grant on hub_post_comments. Every comment
 * and reply goes through the `moderate-hub-comment` edge function
 * (pre-filter + DeepSeek, fail-open-to-flagged, service-role insert).
 *
 * ISOLATION: hub_post_comments only.
 */

export const MAX_HUB_COMMENT = 3000;

export type HubCommentModerationStatus = "pending" | "approved" | "flagged" | "rejected";

export interface HubComment {
  id: string;
  post_id: string;
  parent_comment_id: string | null;
  author_id: string;
  body: string;
  moderation_status: HubCommentModerationStatus;
  moderation_reason: string | null;
  is_deleted: boolean;
  edited_at?: string | null;
  created_at: string;

}

export interface HubCommentThread {
  comment: HubComment;
  replies: HubComment[];
}

export function useHubComments(postId: string, enabled: boolean) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const qc = useQueryClient();
  const { blockedIds } = useBlockedUserIds();
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const key = useMemo(
    () => ["hub-comments", postId, userId ?? "anon", blockedIds.join(",")],
    [postId, userId, blockedIds],
  );

  const query = useQuery({
    queryKey: key,
    enabled: enabled && !!postId,
    staleTime: 15_000,
    queryFn: async (): Promise<HubComment[]> => {
      let q = supabase
        .from("hub_post_comments")
        .select("*")
        .eq("post_id", postId)
        .eq("is_deleted", false)
        .order("created_at", { ascending: true })
        .limit(200);

      // Server-side: drop comments authored by blocked users.
      if (blockedIds.length) q = q.not("author_id", "in", `(${blockedIds.join(",")})`);

      if (userId) q = q.or(`moderation_status.eq.approved,author_id.eq.${userId}`);
      else q = q.eq("moderation_status", "approved");

      const { data, error } = await q;
      if (error) throw error;
      return ((data ?? []) as HubComment[]).filter(
        (c) => c.moderation_status !== "rejected" || c.author_id === userId,
      );
    },
  });

  /** Top-level comments with their (one level of) replies attached. */
  const threads = useMemo<HubCommentThread[]>(() => {
    const rows = query.data ?? [];
    const tops = rows.filter((c) => !c.parent_comment_id);
    return tops.map((comment) => ({
      comment,
      replies: rows.filter((r) => r.parent_comment_id === comment.id),
    }));
  }, [query.data]);

  const submit = useCallback(
    async (body: string, parentCommentId?: string | null): Promise<boolean> => {
      setError(null);
      setNotice(null);
      setPosting(true);
      try {
        const { data, error: fnError } = await supabase.functions.invoke("moderate-hub-comment", {
          body: { post_id: postId, body, parent_comment_id: parentCommentId ?? null },
        });
        if (fnError) throw fnError;

        if (data?.verdict === "block") {
          setError(data.reason || "This comment doesn't meet the LR Hub guidelines.");
          return false;
        }
        if (data?.comment) {
          qc.setQueryData<HubComment[]>(key, (old) => [...(old ?? []), data.comment as HubComment]);
          qc.invalidateQueries({ queryKey: ["hub-feed"] });
          if (data.verdict === "flag") {
            setNotice(data.reason || "Your comment is in review and will appear once approved.");
          }
          return true;
        }
        setError(data?.error || "Could not post your comment. Try again.");
        return false;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not post your comment. Try again.");
        return false;
      } finally {
        setPosting(false);
      }
    },
    [key, postId, qc],
  );

  /**
   * Edit an existing comment. The new text is re-moderated by the SAME edge
   * function; ownership is verified server-side (author_id = caller).
   */
  const edit = useCallback(
    async (commentId: string, body: string): Promise<boolean> => {
      setError(null);
      setNotice(null);
      setPosting(true);
      try {
        const { data, error: fnError } = await supabase.functions.invoke("moderate-hub-comment", {
          body: { comment_id: commentId, post_id: postId, body },
        });
        if (fnError) throw fnError;

        if (data?.verdict === "block") {
          setError(data.reason || "This comment doesn't meet the LR Hub guidelines.");
          return false;
        }
        if (data?.comment) {
          const updated = data.comment as HubComment;
          // Guard against an outdated function deploy answering with a NEW row
          // instead of the updated one (which would look like a silent revert).
          if (updated.id !== commentId) {
            qc.invalidateQueries({ queryKey: key });
            setError("Your edit couldn't be applied. Refresh and try again.");
            return false;
          }
          qc.setQueryData<HubComment[]>(key, (old) =>
            (old ?? []).map((c) => (c.id === updated.id ? updated : c)),
          );
          if (data.verdict === "flag") {
            setNotice(data.reason || "Your edit is in review and will appear once approved.");
          }
          return true;
        }
        setError(data?.error || "Could not save your changes. Try again.");
        return false;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not save your changes. Try again.");
        return false;
      } finally {
        setPosting(false);
      }
    },
    [key, postId, qc],
  );

  /** Drops a soft-deleted comment (and its replies) from the open thread. */
  const removeLocal = useCallback(
    (commentId: string) => {
      qc.setQueryData<HubComment[]>(key, (old) =>
        (old ?? []).filter((c) => c.id !== commentId && c.parent_comment_id !== commentId),
      );
      qc.invalidateQueries({ queryKey: ["hub-feed"] });
    },
    [key, qc],
  );

  return {
    threads,
    count: (query.data ?? []).filter((c) => c.moderation_status === "approved").length,
    isLoading: query.isLoading,
    isError: query.isError,
    posting,
    error,
    notice,
    clearNotices: () => {
      setError(null);
      setNotice(null);
    },
    submit,
    edit,
    removeLocal,
  };

}

export default useHubComments;
